































































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import heic2any from "heic2any";
import FrontOfNationalSvg from "@/modules/national/component/FrontOfNationalSvg.vue";
import FrontOfNationalDesktopSvg from "@/modules/national/component/FrontOfNationalDesktopSvg.vue";
import PreviousPageArrow from "@/components/PreviousPageArrow.vue";
import { base64toBlob } from "@/utils/index";
import {
  DocumentSide,
  DocumentType,
  UploadPayload,
} from "@/modules/document/types";
import { namespace } from "vuex-class";
import Loader from "@/components/svg/Loader.vue";
import { NationalIdType } from "../types";
import router from "@/router";
import axios from "axios";

const Document = namespace("document");
const Trn = namespace("trn");
const NationalId = namespace("nationalId");

@Component({
  metaInfo() {
    return { title: "Orba One | Take Your National ID" };
  },
  components: {
    "front-of-national-svg": FrontOfNationalSvg,
    "front-of-national-desktop-svg": FrontOfNationalDesktopSvg,
    "previous-page-arrow": PreviousPageArrow,
    loader: Loader,
  },
})
export default class CaptureFrontNationalId extends Vue {
  @Document.Action("upload") upload!: (payload: UploadPayload) => Promise<{}>;
  @Trn.Getter("getTrn") trn!: string;
  @Document.Getter("getCountryCode") countryCode!: string;
  @NationalId.Getter("getNationalIdType") nationalIdType!: NationalIdType;
  @NationalId.Action("setNationalIdFrontBlob") setNationalIdFrontBlob!: (
    payload: Blob
  ) => void;

  @Ref() previewDocument!: HTMLImageElement;

  base64Image = "";
  isImageLoaded = false;
  isUploading = false;
  isUploadingError = false;
  isValidationError = false;
  imageBlob?: Blob;
  errorMessage = "";

  async mounted() {
    try {
      if (Vue.prototype.$useAudioInstructions) {
        Vue.prototype.$audioConfigs.stopTypeOfNationalId?.();
        const audio = Vue.prototype.$audioConfigs.playNationalIdFront;
        if (audio && audio.src) {
          await audio.play();
        } else {
          console.warn("Audio source missing or unsupported");
        }
      }
    } catch (err) {
      console.warn("Audio load failed:", err);
    }
  }

  async previewImage(files: File[]) {
    const file = files?.[0];
    if (!file) return;

    try {
      let blobToPreview: Blob = file;

      // ✅ Convert HEIC to JPEG if needed
      if (
        file.type === "image/heic" ||
        file.name.endsWith(".heic") ||
        file.type === "image/heif"
      ) {
        const converted = await heic2any({ blob: file, toType: "image/jpeg" });
        blobToPreview = converted as Blob;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event?.target?.result) {
          this.base64Image = event.target.result as string;
          this.imageBlob = await base64toBlob(this.base64Image);

          this.setNationalIdFrontBlob(this.imageBlob);
          this.isImageLoaded = true;

          if (Vue.prototype.$useAudioInstructions) {
            Vue.prototype.$audioConfigs.stopNationalIdFront?.();
            await Vue.prototype.$audioConfigs.playNationalIdFrontReadability?.();
          }
        }
      };
      reader.readAsDataURL(blobToPreview);
    } catch (err) {
      console.error("Error processing image:", err);
      this.showErrorToast(
        "An error occurred while loading the image. Please try again."
      );
    }
  }

  retryImage() {
    this.isImageLoaded = false;
    this.isValidationError = false;
  }

  confirmImage() {
    if (this.nationalIdType === NationalIdType.NEW) {
      this.isUploading = true;
      router.push({ name: "captureNationalBack", query: this.$route.query });
      this.isUploading = false;
    } else {
      this.uploadNationalFront();
    }
  }

  async uploadNationalFront() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");

    if (!publicKey || !applicantId) return;

    const formData = new FormData();
    formData.append("applicantId", applicantId);
    formData.append("publicKey", publicKey);
    formData.append("type", DocumentType.NATIONALID);
    formData.append("side", DocumentSide.FRONT);
    formData.append("TaxId", this.trn);
    formData.append("countryCode", this.countryCode);

    if (this.imageBlob) {
      formData.append(
        "file",
        this.imageBlob,
        `national_front_${applicantId}_${publicKey}.png`
      );
    }

    this.isUploading = true;
    this.isUploadingError = false;

    this.upload(formData)
      .then(() => {
        this.uploadNationalIdFront();
        this.isUploading = false;
        this.$router.push({ name: "captureNationalBack" });
      })
      .catch((err) => {
        console.error("Upload failed:", err);

        if (Vue.prototype.$useAudioInstructions) {
          Vue.prototype.$audioConfigs.stopNationalIdFrontReadability?.();
          Vue.prototype.$audioConfigs.playNationalIdFrontFailed?.();
        }

        this.isValidationError = true;
        this.isUploading = false;
        this.isUploadingError = true;

        const apiError = err?.response?.data?.errors?.[0];
        if (apiError?.code === "applicant_already_processed") {
          this.showErrorToast("Applicant is already processed.");
        } else {
          this.showErrorToast(
            apiError?.message || "Failed to validate the ID. Please try again."
          );
        }
      });
  }

  showErrorToast(message: string) {
    this.isValidationError = true;
    this.errorMessage = message;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.isValidationError = false;
    }, 5000);
  }

  isMobile(): boolean {
    return screen.width <= 760;
  }

  uploadNationalIdFront() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");
    const magicLink = params.get("magic");
    const progressionStep = 16;

    if ((publicKey && applicantId) || magicLink) {
      axios.put(
        `${process.env.VUE_APP_API_URL}/applicants/${applicantId}/verification-progress-step`,
        {
          publicKey,
          progressionStep,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    }
  }
}
